import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Check, X, Inbox, Trash2, User, SearchX, Search } from "lucide-react";
import { useKaraoke } from "../../store/KaraokeStore";
import { approveSubmission, deleteSubmission, clearSubmissions } from "../../lib/api";
import { toast } from "sonner";

const PendingSubmissionsDialog = ({ open, onOpenChange }) => {
  const { pendingSubmissions, addToQueue, refreshSubmissions, startSearchRequest } = useKaraoke();

  const onApprove = async (sub) => {
    if (!sub.videoId) {
      toast.error("Esta es una solicitud de búsqueda", { description: "Usa 'Buscar' para encontrar una versión." });
      return;
    }
    const ok = addToQueue(
      {
        videoId: sub.videoId,
        title: sub.title,
        channelTitle: sub.channelTitle,
        thumbnail: sub.thumbnail,
        duration: sub.duration,
      },
      sub.singer,
      sub.dedication || null
    );
    if (!ok) return;
    try {
      await approveSubmission(sub.id);
      await deleteSubmission(sub.id);
      toast.success(`"${sub.singer}" añadido a la cola`);
      refreshSubmissions();
    } catch (e) {
      toast.error("Error al aprobar");
    }
  };

  const onSearch = (sub) => {
    startSearchRequest(sub); // injects the query into the console SearchPanel + keeps requester info
    onOpenChange(false);
    toast("Buscando en la consola...", { description: sub.query || sub.title });
  };

  const onReject = async (id) => {
    try {
      await deleteSubmission(id);
      toast("Solicitud rechazada");
      refreshSubmissions();
    } catch (e) {
      toast.error("Error al rechazar");
    }
  };

  const onClearAll = async () => {
    try {
      await clearSubmissions("pending");
      toast("Bandeja vaciada");
      refreshSubmissions();
    } catch (e) {
      toast.error("Error al limpiar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border max-w-lg max-h-[80vh]" data-testid="pending-submissions-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                <Inbox className="w-5 h-5 text-secondary" />
                Solicitudes de invitados
              </DialogTitle>
              <DialogDescription>
                {pendingSubmissions.length === 0
                  ? "No hay solicitudes pendientes."
                  : `${pendingSubmissions.length} canción(es) esperando aprobación.`}
              </DialogDescription>
            </div>
            {pendingSubmissions.length > 0 && (
              <button
                onClick={onClearAll}
                className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"
                data-testid="clear-all-submissions"
              >
                <Trash2 className="w-3 h-3" /> Vaciar
              </button>
            )}
          </div>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[55vh] -mx-6 px-6 space-y-2">
          {pendingSubmissions.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              Comparte el código QR con tus invitados.
            </div>
          ) : (
            pendingSubmissions.map((sub, idx) => {
              const isSearch = sub.request_type === "search_request" || !sub.videoId;
              return (
              <div
                key={sub.id}
                data-testid={`pending-submission-${idx}`}
                className="flex items-center gap-3 border border-border rounded-md p-3 hover:border-secondary/40 transition-colors"
              >
                {isSearch ? (
                  <div className="w-16 h-10 rounded-sm shrink-0 bg-secondary/10 border border-secondary/30 flex items-center justify-center">
                    <SearchX className="w-5 h-5 text-secondary" />
                  </div>
                ) : (
                  <img src={sub.thumbnail} alt="" className="w-16 h-10 object-cover rounded-sm shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  {isSearch ? (
                    <>
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-secondary bg-secondary/10 border border-secondary/30 rounded px-1 py-0.5" data-testid={`search-request-badge-${idx}`}>
                        <Search className="w-2.5 h-2.5" /> Solicitud de búsqueda
                      </span>
                      <p className="text-sm font-semibold leading-tight mt-1">
                        <span className="text-muted-foreground font-normal text-xs">Texto buscado: </span>
                        {sub.query || sub.title}
                      </p>
                      <p className="text-xs text-secondary mt-0.5 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span className="font-medium truncate">{sub.singer}</span>
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold truncate leading-tight">{sub.title}</p>
                      <p className="text-xs text-secondary mt-0.5 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span className="font-medium truncate">{sub.singer}</span>
                        {sub.duration && <span className="text-muted-foreground font-mono ml-1">· {sub.duration}</span>}
                      </p>
                    </>
                  )}
                  {sub.dedication && (
                    <p
                      className="text-[11px] text-primary/85 italic mt-1 truncate"
                      title={sub.dedication}
                      data-testid={`pending-dedication-${idx}`}
                    >
                      ♥ "{sub.dedication}"
                    </p>
                  )}
                </div>
                {isSearch ? (
                  <Button
                    size="sm"
                    onClick={() => onSearch(sub)}
                    data-testid={`search-submission-${idx}`}
                    className="bg-secondary text-secondary-foreground hover:bg-secondary/90 h-8 px-3 gap-1.5"
                    title="Buscar esta canción en la consola"
                  >
                    <Search className="w-4 h-4" /> Buscar
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => onApprove(sub)}
                    data-testid={`approve-submission-${idx}`}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3"
                    title="Aprobar y añadir a la cola"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onReject(sub.id)}
                  data-testid={`reject-submission-${idx}`}
                  className="text-muted-foreground hover:text-destructive h-8 w-8 p-0"
                  title="Rechazar"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PendingSubmissionsDialog;
